import React from "react";
import { Alert, Flex, Modal, Spin } from "antd";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useTranslation } from "react-i18next";
import type { ApplicationModel } from "../../../../viewModels/Applications/ApplicationModel";
import slice from "../../reducer";
import { makeSelectCargoPlanning } from "../../selectors";
import type { CargoPlanningState } from "../../cargoPlanningTypes";
import type { RootState } from "../../../../configureStore";

const CargoPlanningContext = React.createContext<CargoPlanningState | undefined>(undefined);
export const useCargoPlanning = () => React.useContext(CargoPlanningContext);

type Props = { application: ApplicationModel; children: React.ReactNode };

const CargoPlanningProvider = ({ application, children }: Props) => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const state = useSelector(makeSelectCargoPlanning);
  const { t } = useTranslation("translations");
  const isFtl = application.type === "FTL";
  const applicationId = application.id;

  React.useEffect(() => {
    const metadata = store.getState().application.cargoPlanning;
    if (isFtl && (metadata.dealId || metadata.syncState === "SYNCING")) {
      dispatch(slice.actions.checkCargoPlanning());
    }
    return () => {
      dispatch(slice.actions.stopCargoPlanning());
      dispatch(slice.actions.closeCargoPlanning());
    };
  }, [applicationId, isFtl, dispatch, store]);

  React.useEffect(() => {
    if (isFtl && state.refreshVersion > 0) dispatch(slice.actions.checkCargoPlanning());
  }, [dispatch, isFtl, state.refreshVersion]);

  React.useEffect(() => {
    if (
      state.modalOpen &&
      state.confirmed &&
      !state.saving &&
      !state.saveError &&
      !state.statusError &&
      !state.loadingUrl &&
      !state.url &&
      !state.urlError
    ) {
      dispatch(slice.actions.loadCargoPlanningLink({ applicationId, session: state.modalSession }));
    }
  }, [
    applicationId,
    dispatch,
    state.modalOpen,
    state.confirmed,
    state.saving,
    state.saveError,
    state.statusError,
    state.loadingUrl,
    state.url,
    state.urlError,
    state.modalSession,
  ]);

  if (!isFtl) return <>{children}</>;

  const blockingError = state.saveError || state.statusError || state.urlError || (!state.dealId && state.syncState === "ERROR");

  return (
    <CargoPlanningContext.Provider value={state}>
      {children}
      <Modal
        open={state.modalOpen}
        title={t("recognizing_cargo_and_dimensions")}
        onCancel={() => dispatch(slice.actions.closeCargoPlanning())}
        footer={null}
        destroyOnHidden
        width={state.url ? "calc(100vw - 32px)" : 640}
        style={{ maxWidth: 1600 }}
      >
        <Flex vertical gap={16}>
          {state.modalOpen && blockingError && <Alert type="error" showIcon title={t("unable_to_recognize_cargo")} />}
          {!blockingError && !state.url && (
            <Flex align="center" justify="center" style={{ minHeight: 180 }} aria-live="polite" aria-busy>
              <Spin description={t("recognizing")} />
            </Flex>
          )}
          {state.url && (
            <iframe
              title={t("recognizing_cargo_and_dimensions")}
              src={state.url}
              referrerPolicy="no-referrer"
              style={{ width: "100%", height: "72vh", border: 0 }}
            />
          )}
        </Flex>
      </Modal>
    </CargoPlanningContext.Provider>
  );
};

export default CargoPlanningProvider;
